import { createContext, useContext, useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { getLoungeProfile, saveLoungeProfile, trackLoungeEvent } from "@/lib/loungeTrack";

export function isPressAccredited() { return !!getLoungeProfile("press")?.email; }
export function isLoungeAccredited(area) { return !!getLoungeProfile(area)?.email; }

// Avvia il download di un URL senza abbandonare la pagina (attachment / nuova scheda).
export function triggerDownload(url) {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const T = {
  it: { title: "Accreditamento Stampa", sub: "Inserisci i tuoi dati per scaricare i materiali ufficiali. Il contatto sarà registrato dall'ufficio stampa Brema 1969.", name: "Nome e Cognome", outlet: "Testata / Rivista", email: "Email", cta: "Accreditati e scarica", required: "Compila tutti i campi", ok: "Accreditamento completato · download in corso" },
  en: { title: "Press Accreditation", sub: "Enter your details to download the official materials. Your contact will be saved by the Brema 1969 press office.", name: "Full name", outlet: "Publication / Magazine", email: "Email", cta: "Accredit & download", required: "Please fill in all fields", ok: "Accreditation complete · download starting" },
  de: { title: "Presse-Akkreditierung", sub: "Geben Sie Ihre Daten ein, um die offiziellen Materialien herunterzuladen. Ihr Kontakt wird vom Pressebüro Brema 1969 gespeichert.", name: "Vor- und Nachname", outlet: "Publikation / Magazin", email: "E-Mail", cta: "Akkreditieren & herunterladen", required: "Bitte alle Felder ausfüllen", ok: "Akkreditierung abgeschlossen · Download startet" },
  fr: { title: "Accréditation Presse", sub: "Saisissez vos informations pour télécharger les documents officiels. Votre contact sera enregistré par le bureau de presse Brema 1969.", name: "Nom et prénom", outlet: "Publication / Magazine", email: "E-mail", cta: "S'accréditer et télécharger", required: "Veuillez remplir tous les champs", ok: "Accréditation terminée · téléchargement en cours" },
  es: { title: "Acreditación de Prensa", sub: "Introduce tus datos para descargar los materiales oficiales. Tu contacto quedará registrado en la oficina de prensa de Brema 1969.", name: "Nombre y apellidos", outlet: "Publicación / Revista", email: "Email", cta: "Acreditarse y descargar", required: "Rellena todos los campos", ok: "Acreditación completada · descarga en curso" },
};
const TA = {
  it: { title: "Accesso Ambassador", sub: "Inserisci i tuoi dati una sola volta per scaricare i materiali e inviare richieste. Il profilo sarà registrato dal team Brema 1969.", name: "Nome e Cognome", outlet: "Profilo Instagram (@handle o link)", email: "Email", cta: "Entra e continua", required: "Compila tutti i campi", ok: "Benvenuto nel team · continuiamo" },
  en: { title: "Ambassador Access", sub: "Enter your details once to download materials and send requests. Your profile will be saved by the Brema 1969 team.", name: "Full name", outlet: "Instagram profile (@handle or link)", email: "Email", cta: "Enter & continue", required: "Please fill in all fields", ok: "Welcome to the team · continuing" },
  de: { title: "Ambassador-Zugang", sub: "Geben Sie Ihre Daten einmalig ein, um Materialien herunterzuladen und Anfragen zu senden. Ihr Profil wird vom Brema 1969 Team gespeichert.", name: "Vor- und Nachname", outlet: "Instagram-Profil (@handle oder Link)", email: "E-Mail", cta: "Eintreten & fortfahren", required: "Bitte alle Felder ausfüllen", ok: "Willkommen im Team · weiter geht's" },
  fr: { title: "Accès Ambassador", sub: "Saisissez vos informations une seule fois pour télécharger les contenus et envoyer des demandes. Votre profil sera enregistré par l'équipe Brema 1969.", name: "Nom et prénom", outlet: "Profil Instagram (@handle ou lien)", email: "E-mail", cta: "Entrer et continuer", required: "Veuillez remplir tous les champs", ok: "Bienvenue dans l'équipe · on continue" },
  es: { title: "Acceso Ambassador", sub: "Introduce tus datos una sola vez para descargar materiales y enviar solicitudes. Tu perfil quedará registrado en el equipo de Brema 1969.", name: "Nombre y apellidos", outlet: "Perfil de Instagram (@handle o enlace)", email: "Email", cta: "Entrar y continuar", required: "Rellena todos los campos", ok: "Bienvenido al equipo · continuamos" },
};

const Ctx = createContext({ gate: (a) => a && a(), accredited: false, area: "press" });
export const usePressGate = () => useContext(Ctx);
export const useLoungeGate = usePressGate;

// mode "press" (nome/testata/email) o "ambassador" (nome/instagram/email). gate(action, track) esegue l'azione
// dopo l'accredito e, se `track` è fornito ({type, item}), registra l'evento in Statistiche & Report.
export function PressGateProvider({ lang = "it", mode = "press", children }) {
  const [open, setOpen] = useState(false);
  const [accredited, setAccredited] = useState(() => isLoungeAccredited(mode));
  const pending = useRef(null);

  const gate = useCallback((action, track) => {
    const run = () => { if (action) action(); if (track) trackLoungeEvent({ area: mode, type: track.type || "download", item: track.item || {}, lang }); };
    if (isLoungeAccredited(mode)) { run(); return; }
    pending.current = run;
    setOpen(true);
  }, [mode, lang]);

  const onAccredited = () => {
    setAccredited(true);
    const a = pending.current;
    pending.current = null;
    setOpen(false);
    setTimeout(() => { if (a) a(); }, 80);
  };

  return (
    <Ctx.Provider value={{ gate, accredited, area: mode }}>
      {children}
      <AccreditationDialog open={open} onOpenChange={setOpen} onAccredited={onAccredited} lang={lang} mode={mode} />
    </Ctx.Provider>
  );
}

function AccreditationDialog({ open, onOpenChange, onAccredited, lang, mode }) {
  const t = (mode === "ambassador" ? TA : T)[lang] || (mode === "ambassador" ? TA : T).it;
  const [form, setForm] = useState({ name: "", outlet: "", email: "" });
  const [saving, setSaving] = useState(false);
  const tid = mode === "ambassador" ? "amb-accredit" : "press-accredit";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.outlet.trim() || !form.email.trim()) { toast.error(t.required); return; }
    setSaving(true);
    try {
      let ref = "";
      try { ref = sessionStorage.getItem("lounge_ref") || ""; } catch { /* ignore */ }
      if (mode === "ambassador") {
        await api.post("/public/lounge/ambassador-accredit", { name: form.name, social: form.outlet, email: form.email, lang, ref });
        saveLoungeProfile("ambassador", { name: form.name, social: form.outlet, email: form.email });
        try { sessionStorage.setItem("brema_lounge_access_ambassador", "1"); } catch { /* ignore */ }
      } else {
        await api.post("/public/lounge/press-accredit", { ...form, lang });
        saveLoungeProfile("press", form);
      }
      toast.success(t.ok);
      onAccredited && onAccredited();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error, please retry");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${tid}-dialog`} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand" /> {t.title}</DialogTitle>
          <DialogDescription>{t.sub}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t.name}</Label>
            <Input data-testid={`${tid}-name`} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t.outlet}</Label>
            <Input data-testid={`${tid}-outlet`} value={form.outlet} onChange={(e) => setForm((p) => ({ ...p, outlet: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t.email}</Label>
            <Input data-testid={`${tid}-email`} type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required />
          </div>
          <Button type="submit" disabled={saving} data-testid={`${tid}-submit`} className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t.cta}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
